import React, { useState } from "react";
import { Box, Card, Typography } from "@mui/material";

import {
    ComposableMap,
    Geographies,
    Geography
} from "react-simple-maps";

// USA states TopoJSON URL
const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// Map state FIPS → Code mapping
const stateCodes = {
    1: "AL", 2: "AK", 4: "AZ", 5: "AR", 6: "CA",
    8: "CO", 9: "CT", 10: "DE", 12: "FL", 13: "GA",
    16: "ID", 17: "IL", 18: "IN", 19: "IA", 20: "KS",
    21: "KY", 22: "LA", 23: "ME", 24: "MD", 25: "MA",
    26: "MI", 27: "MN", 28: "MS", 29: "MO", 30: "MT",
    31: "NE", 32: "NV", 33: "NH", 34: "NJ", 35: "NM",
    36: "NY", 37: "NC", 38: "ND", 39: "OH", 40: "OK",
    41: "OR", 42: "PA", 44: "RI", 45: "SC", 46: "SD",
    47: "TN", 48: "TX", 49: "UT", 50: "VT", 51: "VA",
    53: "WA", 54: "WV", 55: "WI", 56: "WY"
};

// EXACT COLOR RANGE (from your image)
const getColor = (value) => {
    if (value === 0) return "#C8C6C4";          // No Data (Gray)
    if (value <= 5000000) return "#E8F3E2";     // $0–$5M (Light Green)
    if (value <= 10000000) return "#D0EBBE";    // $5–$10M
    if (value <= 20000000) return "#B7E29A";    // $10–$20M
    if (value <= 35000000) return "#99D56F";    // $20–$35M
    if (value <= 50000000) return "#6BB542";    // $35–$50M
    return "#4C8C17";                           // $50M+
};

const formatValue = (val) => {
    if (val >= 1000000) return `$${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `$${(val / 1000).toFixed(1)}K`;
    return `$${val}`;
};

const Maps = ({ usaData = {}, thickBorderStates = [], legendData = [], title }) => {
    const [tooltip, setTooltip] = useState({ content: "", x: 0, y: 0, show: false });

    return (
        <Card
            sx={{
                borderRadius: "12px !important",
                background: "#fff",
                border: "1px solid #e5e5e5",
                boxShadow: "0px 1px 12px rgba(0, 0, 0, 0.1)",
                height: "100%",
                position: "relative",
                overflow: "visible",
            }}
        >
            {/* TITLE */}
            <Box
                sx={{
                    position: "absolute",
                    top: "-10px",
                    left: "12px",
                    right: "12px",
                    background: "#115f9c",
                    height: "36px",
                    borderRadius: "4px !important",
                    display: "flex",
                    alignItems: "center",
                    px: "12px",
                    color: "#fff",
                    fontSize: { xs: 12, sm: 12, md: 16 },
                    fontWeight: 200,
                    boxShadow: "0px 1px 16px rgba(128, 203, 255, 0.5)",
                    zIndex: 10
                }}
            >
                {title}
            </Box>

            {/* MAP */}
            <Box sx={{ p: 3, display: "flex", flexDirection: "column", alignItems: "center", position: "relative" }}>
                <ComposableMap
                    projection="geoAlbersUsa"
                    style={{ width: "100%", height: "100%" }}
                >
                    <Geographies geography={geoUrl}>
                        {({ geographies }) =>
                            geographies.map((geo) => {
                                const code = stateCodes[geo.id];
                                const value = usaData[code] || 0;
                                const isThick = thickBorderStates.includes(code);

                                return (
                                    <Geography
                                        key={geo.rsmKey}
                                        geography={geo}
                                        fill={getColor(value)}
                                        stroke="#000"
                                        strokeWidth={isThick ? 3.2 : 0.6}
                                        strokeLinejoin="round"
                                        strokeLinecap="round"
                                        style={{
                                            default: { outline: "none" },
                                            hover: { outline: "none", cursor: "pointer" },
                                            pressed: { outline: "none" },
                                        }}
                                        onMouseEnter={(e) => {
                                            setTooltip({
                                                show: true,
                                                content: `${geo.properties.name}: ${formatValue(value)}`,
                                                x: e.pageX,
                                                y: e.pageY
                                            });
                                        }}
                                        onMouseMove={(e) => {
                                            setTooltip(prev => ({
                                                ...prev,
                                                x: e.pageX,
                                                y: e.pageY
                                            }));
                                        }}
                                        onMouseLeave={() => {
                                            setTooltip(prev => ({ ...prev, show: false }));
                                        }}
                                    />
                                );
                            })
                        }
                    </Geographies>
                </ComposableMap>

                {/* CUSTOM TOOLTIP */}
                {tooltip.show && (
                    <Box
                        sx={{
                            position: "fixed",
                            top: tooltip.y + 15,
                            left: tooltip.x + 15,
                            background: "rgba(0, 0, 0, 0.8)",
                            color: "#fff",
                            padding: "6px 12px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            pointerEvents: "none",
                            zIndex: 9999,
                            boxShadow: "0px 2px 8px rgba(0,0,0,0.3)",
                            whiteSpace: "nowrap"
                        }}
                    >
                        {tooltip.content}
                    </Box>
                )}

                {/* LEGEND */}
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "center",
                        gap: 3,
                        mt: 3,
                    }}
                >
                    {legendData.map((item, i) => (
                        <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box
                                sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: "50%",
                                    backgroundColor: item.color,
                                }}
                            />
                            <Typography sx={{ fontSize: 10, color: "#111111", fontWeight: 200 }} >{item.label}</Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Card>
    );
};

export default Maps;
